import { getPlayer, resolveActiveGameRun } from "@dailywars/core/db";
import type { MedalScoreThresholds } from "@dailywars/core/db";
import type { MapAccessDeniedReason } from "@dailywars/core/entitlements/types";
import { createServerFn } from "@tanstack/react-start";

import { setRouteAction } from "#/lib/requestLogger.server";
import { loadPlayMapPageInputSchema } from "#/lib/serverInputSchemas";
import { canPlayerAccessMap } from "#/server/game/mapAccessLogic.server";
import { resolveRequestGameContext } from "#/server/game/requestGameContext.server";

import type { StartRunResponse } from "./contracts";
import { loadMapMedalThresholds } from "./mapMedalThresholds.server";
import { startMapRun } from "./startRunCore";

async function readyPlayMapPage(
  run: StartRunResponse,
): Promise<
  { status: "ready" } & StartRunResponse & { medalThresholds: MedalScoreThresholds | null }
> {
  const medalThresholds = await loadMapMedalThresholds(run.mapId);
  return { status: "ready", ...run, medalThresholds };
}

type PlayMapPageAccessDenied = {
  status: "access-denied";
  mapId: string;
  reason: MapAccessDeniedReason;
};

export type PlayMapPageTutorialPrompt = {
  status: "tutorial-prompt";
  playerId: string;
  mapId: string;
};

export type PlayMapPageReady = {
  status: "ready";
  medalThresholds: MedalScoreThresholds | null;
} & StartRunResponse;

export type PlayMapPageLoaderResult =
  | PlayMapPageAccessDenied
  | PlayMapPageTutorialPrompt
  | PlayMapPageReady;

async function shouldPromptForTutorial({
  isAnonymous,
  playerId,
}: {
  isAnonymous: boolean;
  playerId: string;
}): Promise<boolean> {
  if (isAnonymous) {
    return true;
  }
  const player = await getPlayer(playerId);
  return player?.hasCompletedTutorial !== true;
}

export const loadPlayMapPage = createServerFn({ method: "GET" })
  .validator(loadPlayMapPageInputSchema)
  .handler(async ({ data }): Promise<PlayMapPageLoaderResult> => {
    setRouteAction("play_map_page");
    const { mapId, playerId: clientPlayerId } = data;
    const { playerId, entitlements, isAnonymous } = await resolveRequestGameContext(clientPlayerId);
    const access = canPlayerAccessMap(mapId, entitlements);

    if (!access.ok) {
      return { status: "access-denied", mapId, reason: access.reason };
    }

    const existing = await resolveActiveGameRun({ playerId, mapId });
    if (existing) {
      return readyPlayMapPage({
        playerId,
        mapId,
        state: existing.state,
        currentDay: existing.state.currentDay,
      });
    }

    if (await shouldPromptForTutorial({ isAnonymous, playerId })) {
      return { status: "tutorial-prompt", playerId, mapId };
    }

    return readyPlayMapPage(await startMapRun({ mapId, playerId: clientPlayerId ?? playerId }));
  });
